import { useState } from "react";
import { PageHeader, EmptyState } from "@/components/Layout";
import { Button, ErrorNote, useCanWrite } from "@/components/ui";
import { ArchivedFirms } from "@/components/archived-firms";
import { OrphanServers, OwnerServers, OwnerToggle, serversSummary, useServersByOwner } from "@/components/owner-servers";
import { ArchiveCatalogo, FirmPanel } from "@/pages/PropFirms";
import { errorMessage } from "@/lib/api";
import { useBrokers } from "@/lib/queries";
import type { BrokerServer, PropFirm } from "@/lib/types";

/*
 * I broker, con i loro server.
 *
 * Stessa forma della pagina Prop firm: l'elenco dei possessori, ognuno
 * espandibile, e dentro i suoi server. Un broker non ha programmi: sono i
 * server a portare le specifiche dei simboli (fuso, valore punto, lot step) da
 * cui il motore calcola la gamba broker.
 */

export function Brokers() {
  const canWrite = useCanWrite();
  const brokers = useBrokers();
  const [panel, setPanel] = useState(false);
  const servers = useServersByOwner("BROKER", brokers.data);

  return (
    <>
      <PageHeader
        title="Broker"
        subtitle="I broker e i loro server MT5: fuso, conti e mappatura dei simboli da cui il motore calcola la gamba broker"
        actions={canWrite && <Button variant="primary" onClick={() => setPanel(true)}>Nuovo broker</Button>}
      />

      {brokers.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : brokers.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(brokers.error)}</ErrorNote>
        </div>
      ) : !brokers.data?.length ? (
        <EmptyState
          title="Nessun broker"
          hint="Aggiungi il broker del conto, poi i suoi server: un server nasce legato al broker da cui lo aggiungi."
          action={canWrite && <Button variant="primary" onClick={() => setPanel(true)}>Nuovo broker</Button>}
        />
      ) : (
        <div className="space-y-4 px-6 py-4">
          {brokers.data.map((b) => (
            <BrokerCard
              key={b.id}
              broker={b}
              servers={servers.ready ? servers.byOwner.get(b.id) ?? [] : null}
              serversError={servers.error}
              canWrite={canWrite}
            />
          ))}
          {servers.ready && <OrphanServers kind="BROKER" servers={servers.orphans} />}
        </div>
      )}

      <ArchivedFirms kind="BROKER" />

      {panel && <FirmPanel kind="BROKER" onClose={() => setPanel(false)} />}
    </>
  );
}

function BrokerCard({
  broker,
  servers,
  serversError,
  canWrite,
}: {
  broker: PropFirm;
  servers: BrokerServer[] | null;
  serversError: unknown;
  canWrite: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  // Finché i server non sono letti non si dice «0 server»: non lo sappiamo.
  const summary = servers ? serversSummary(servers) : "server in lettura…";

  return (
    <section className="card pair-side-broker">
      <header className="flex items-center justify-between gap-3 px-4 py-3">
        <OwnerToggle open={open} onToggle={() => setOpen((o) => !o)} title={broker.name} summary={summary} />
        {canWrite && (
          <span className="inline-flex flex-wrap items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              Modifica
            </Button>
            <ArchiveCatalogo kind="broker" id={broker.id} label={broker.name} />
          </span>
        )}
      </header>
      {open && <OwnerServers ownerId={broker.id} ownerName={broker.name} kind="BROKER" servers={servers} error={serversError} />}
      {editing && <FirmPanel kind="BROKER" firm={broker} onClose={() => setEditing(false)} />}
    </section>
  );
}
